import React, { useEffect, useState } from "react";
import api, { API, getToken } from "../../lib/api";
import PortalEstimates from "./PortalEstimates";
import PortalReports, { PortalImg } from "./PortalReports";
import PortalSprays from "./PortalSprays";
import PortalPhotos from "./PortalPhotos";
import PortalBudget from "./PortalBudget";
import PortalWater from "./PortalWater";
import PortalWeather from "./PortalWeather";
import PortalTasks from "./PortalTasks";
import PortalMildew from "./PortalMildew";
import PortalRequests from "./PortalRequests";
import { Grape, Leaf, SprayCan, LayoutGrid, PiggyBank, Camera, Droplets, CloudSun, ListChecks, Home, Map as MapIcon, Hammer } from "lucide-react";

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: Home },
  { id: "blocks", label: "Block Map", icon: LayoutGrid },
  { id: "tasks", label: "To-Do", icon: ListChecks },
  { id: "reports", label: "Scouting Reports", icon: Leaf },
  { id: "estimates", label: "Crop Estimates", icon: Grape },
  { id: "weather", label: "Weather", icon: CloudSun },
  { id: "water", label: "Irrigation", icon: Droplets },
  { id: "photos", label: "Photo Timeline", icon: Camera },
  { id: "sprays", label: "Spray Info", icon: SprayCan },
  { id: "budget", label: "Budget vs Actual", icon: PiggyBank },
];

export default function ClientPortal({ initialTab = "dashboard" }) {
  const [tab, setTab] = useState(initialTab);
  const [overview, setOverview] = useState(null);

  useEffect(() => { setTab(initialTab); }, [initialTab]);
  useEffect(() => {
    document.querySelector('[data-testid="shell-main"]')?.scrollTo({ top: 0 });
  }, [tab]);

  useEffect(() => { api.get("/portal/overview").then(({ data }) => setOverview(data)); }, []);
  if (!overview) return <div className="p-8 font-bold uppercase text-sm">Loading your vineyards…</div>;

  return (
    <div className="space-y-5" data-testid="client-portal">
      <div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>My Vineyards</h1>
        <p className="text-stone-500 text-sm mt-1">{overview.blocks.length} block{overview.blocks.length === 1 ? "" : "s"} across {overview.ranch_numbers.length} ranch{overview.ranch_numbers.length === 1 ? "" : "es"} · read-only view</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-5">
        <nav className="lg:w-56 shrink-0" data-testid="portal-side-menu">
          <div className="bg-white border border-stone-200 flex lg:flex-col overflow-x-auto">
            {TABS.map((t) => (
              <button key={t.id} data-testid={`portal-tab-${t.id}`} onClick={() => setTab(t.id)}
                className={`h-12 px-4 text-xs font-bold uppercase flex items-center gap-2.5 whitespace-nowrap text-left lg:w-full border-b border-stone-100 last:border-b-0 ${tab === t.id ? "bg-[#1F3D34] text-white" : "text-stone-600 hover:bg-stone-50"}`}>
                <t.icon size={15} className="shrink-0" /> {t.label}
              </button>
            ))}
          </div>
        </nav>

        <div className="flex-1 min-w-0">
          {tab === "dashboard" && (
            <div className="space-y-6" data-testid="portal-dashboard">
              <LatestReport onViewFull={() => setTab("reports")} />
              <PortalMildew />
              <div>
                <div className="text-xs font-bold uppercase text-stone-500 mb-2 flex items-center gap-2"><ListChecks size={14} /> To-Do</div>
                <PortalTasks />
              </div>
              <div>
                <div className="text-xs font-bold uppercase text-stone-500 mb-2 flex items-center gap-2"><CloudSun size={14} /> Weather</div>
                <PortalWeather />
              </div>
            </div>
          )}
          {tab === "blocks" && <Blocks blocks={overview.blocks} />}
          {tab === "photos" && <PortalPhotos />}
          {tab === "estimates" && <PortalEstimates />}
          {tab === "reports" && <PortalReports />}
          {tab === "sprays" && <PortalSprays />}
          {tab === "water" && <PortalWater />}
          {tab === "weather" && <PortalWeather />}
          {tab === "tasks" && <PortalTasks />}
          {tab === "requests" && <PortalRequests />}
          {tab === "budget" && <PortalBudget />}
        </div>
      </div>
    </div>
  );
}

function LatestReport({ onViewFull }) {
  const [detail, setDetail] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: reps } = await api.get("/portal/reports");
        if (reps.length > 0) {
          const { data } = await api.get(`/portal/reports/${reps[0].id}`);
          setDetail(data);
        }
      } catch (e) { console.warn("Latest report load failed", e); }
      setLoaded(true);
    })();
  }, []);

  if (!loaded) return <div className="bg-white border border-stone-200 h-32 animate-pulse" data-testid="latest-report-loading" />;
  if (!detail) return null;

  const { report, photos } = detail;
  return (
    <div data-testid="portal-latest-report">
      <div className="text-xs font-bold uppercase text-stone-500 mb-2 flex items-center gap-2"><Leaf size={14} /> Latest Scouting Report</div>
      <div className="bg-white border border-stone-200 p-5 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <div className="font-bold text-lg" style={{ fontFamily: "'Fraunces', serif" }}>{report.vineyard_name} — Field Report</div>
            <div className="text-xs text-stone-500">{report.report_date} · v{report.version} · Prepared by {report.prepared_by}</div>
          </div>
          <button data-testid="latest-report-view-full" onClick={onViewFull}
            className="h-9 px-4 bg-[#1F3D34] text-white font-bold uppercase text-xs">View Full Report</button>
        </div>
        {report.overview && (
          <div>
            <div className="font-bold uppercase text-[10px] text-stone-500 mb-1">Overview</div>
            <p className="text-sm whitespace-pre-wrap line-clamp-4">{report.overview}</p>
          </div>
        )}
        {(photos || []).length > 0 && (
          <div>
            <div className="font-bold uppercase text-[10px] text-stone-500 mb-2">Field Photos</div>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {photos.slice(0, 6).map((p) => (
                <div key={p.id}>
                  <PortalImg photoId={p.id} className="w-full h-20 object-cover" />
                  {p.caption && <div className="text-[10px] text-stone-500 mt-0.5 truncate">{p.caption}</div>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function RanchMap() {
  const [maps, setMaps] = useState([]);
  const [urls, setUrls] = useState({});

  useEffect(() => {
    let revoked = [];
    api.get("/portal/maps").then(async ({ data }) => {
      setMaps(data);
      const token = getToken();
      const out = {};
      for (const m of data) {
        try {
          const res = await fetch(`${API}/portal/maps/${m.id}/image`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
          if (res.ok) { const u = URL.createObjectURL(await res.blob()); out[m.id] = u; revoked.push(u); }
        } catch (e) { console.warn("Ranch map image load failed", e); }
        setUrls({ ...out });
      }
    });
    return () => revoked.forEach((u) => URL.revokeObjectURL(u));
  }, []);

  if (maps.length === 0) return null;
  return (
    <div className="space-y-3" data-testid="portal-ranch-maps">
      {maps.map((m) => (
        <div key={m.id} className="bg-white border border-stone-200" data-testid={`portal-ranch-map-${m.id}`}>
          <div className="px-4 py-2.5 border-b border-stone-100 flex items-center gap-2 text-xs font-bold uppercase text-stone-500">
            <MapIcon size={14} /> {m.title || "Ranch Map"} <span className="normal-case font-normal text-stone-400">· {m.ranch_number}</span>
          </div>
          {urls[m.id]
            ? <img src={urls[m.id]} alt={m.title || "Ranch map"} className="w-full" />
            : <div className="h-64 bg-stone-100 animate-pulse" />}
        </div>
      ))}
    </div>
  );
}

function Blocks({ blocks }) {
  return (
    <div className="space-y-5">
      <RanchMap />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="portal-blocks">
      {blocks.map((b) => (
        <div key={b.id} data-testid={`portal-block-${b.id}`} className="bg-white border border-stone-200 p-4 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="font-bold text-lg" style={{ fontFamily: "'Fraunces', serif" }}>{b.block || b.name}</div>
              <div className="text-xs text-stone-500">{b.ranch_number} · {b.name}</div>
            </div>
            <span className={`text-[10px] font-bold uppercase px-2 py-1 ${b.farming_type === "Organic" ? "bg-[#7C9673] text-white" : "bg-stone-100 text-stone-600 border border-stone-200"}`}>
              {b.farming_type || "Conventional"}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div><div className="text-[10px] font-bold uppercase text-stone-400">Variety</div>{b.variety || "—"}</div>
            <div><div className="text-[10px] font-bold uppercase text-stone-400">Clone</div>{b.clone || "—"}</div>
            <div><div className="text-[10px] font-bold uppercase text-stone-400">Rootstock</div>{b.rootstock || "—"}</div>
            <div><div className="text-[10px] font-bold uppercase text-stone-400">Acres</div>{b.acres ?? "—"}</div>
            <div><div className="text-[10px] font-bold uppercase text-stone-400">Vines / Acre</div>{b.vines_per_acre ?? "—"}</div>
            <div><div className="text-[10px] font-bold uppercase text-stone-400">Total Vines</div>{b.vines_per_acre && b.acres ? Math.round(b.vines_per_acre * b.acres).toLocaleString() : "—"}</div>
            <div className="col-span-2"><div className="text-[10px] font-bold uppercase text-stone-400">Location</div>{[b.city, b.county, b.state].filter(Boolean).join(", ") || "—"}</div>
          </div>
        </div>
      ))}
      {blocks.length === 0 && (
        <div className="col-span-full bg-white border border-stone-200 p-10 text-center text-stone-400 font-bold uppercase text-sm" data-testid="portal-blocks-empty">
          No blocks assigned to your account yet — contact your vineyard manager
        </div>
      )}
      </div>
    </div>
  );
}
